import { formatMonthYear, DEFAULT_CALENDAR, type CalendarSystem } from './date'

export interface BillingPeriodOptions {
  /** BCP-47 locale for the month name (e.g. 'en', 'th'). Defaults to 'en'. */
  locale?: string
  /** 'gregorian' (CE) or 'buddhist' (BE, +543). Defaults to gregorian. */
  calendar?: CalendarSystem
}

// Generate the last 12 billing periods. The label honours the account's
// calendar era + UI locale (B-020) so Thai/Buddhist users see e.g.
// "มิถุนายน 2569" instead of a hardcoded en-US "June 2026". The `value`
// stays YYYY-MM (locale-agnostic) for the API/query layer.
export function generateBillingPeriods(
  options: BillingPeriodOptions = {},
): { value: string; label: string }[] {
  const { locale = 'en', calendar = DEFAULT_CALENDAR } = options
  const periods: { value: string; label: string }[] = []
  const now = new Date()
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = formatMonthYear(d, { locale, calendar })
    periods.push({ value, label })
  }
  return periods
}

// Format a Date's own local year/month/day as 'YYYY-MM-DD'. Unlike
// toISOString().slice(0, 10) (which reads the UTC calendar day), this reads
// the date's own local fields, so it matches the local y/m/d the caller
// constructed it with in every runner timezone. See DAT-1: getMonthRange
// used to build `start`/`end` at local midnight and then take the UTC slice,
// which in a zone ahead of UTC (e.g. Asia/Bangkok, UTC+7) shifted both ends
// back a day.
function toLocalDateString(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function getMonthRange(period: string): { from: string; to: string } {
  const [year, month] = period.split('-').map(Number)
  const start = new Date(year, month - 1, 1)
  const end = new Date(year, month, 0) // last day of month
  return {
    from: toLocalDateString(start),
    to: toLocalDateString(end),
  }
}
